'use client';

import { useEffect, useState } from 'react';
import { ArrowDownToLine, ArrowUpToLine, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import DepositModal, { type DepositModalProps } from './DepositModal';
import WithdrawModal, { type WithdrawModalProps } from './WithdrawModal';
import HistoryDrawer, { type TransactionHistoryPanelProps } from './HistoryDrawer';
import { useNotificationStore } from '@/stores/notificationStore';

export interface DepositWithdrawalActionsProps {
  /** 点击充值时额外回调（弹窗由组件内部管理） */
  showHistoryButton?: boolean;
  onDeposit?: () => void;
  /** 点击提现时额外回调（弹窗由组件内部管理） */
  onWithdraw?: () => void;
  /** 交易历史入口（时钟按钮） */
  onHistory?: () => void;
  /** 透传给充值弹窗的数据（地址、二维码、链/币信息） */
  depositProps?: Omit<DepositModalProps, 'open' | 'onClose'>;
  /** 透传给提现弹窗的数据（余额、手续费、提交回调） */
  withdrawProps?: Omit<WithdrawModalProps, 'open' | 'onClose'>;
  /** 透传给交易历史面板的数据（Telegram 求助链接） */
  historyProps?: TransactionHistoryPanelProps;
  className?: string;
}

/**
 * 充值 / 提现 / 交易历史 操作按钮行。
 * Figma: Pythra 产品版本管理 node 898-22280
 */
export default function DepositWithdrawalActions({
  onDeposit,
  onWithdraw,
  onHistory,
  depositProps,
  withdrawProps,
  historyProps,
  showHistoryButton = false,
  className,
}: DepositWithdrawalActionsProps) {
  const { t } = useTranslation();
  const [activeModal, setActiveModal] = useState<'deposit' | 'withdraw' | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const notificationOpen = useNotificationStore((state) => state.isOpen);

  // 通知面板打开时关闭交易历史抽屉，避免两个右侧面板叠加
  useEffect(() => {
    if (notificationOpen) setHistoryOpen(false);
  }, [notificationOpen]);

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <button
        type="button"
        onClick={() => {
          setActiveModal('deposit');
          onDeposit?.();
        }}
        className="flex h-11 px-5 items-center justify-center gap-1 rounded-lg bg-[#A772FF] text-sm font-normal leading-4.5 text-white transition-colors hover:bg-[#9A5EF5]"
      >
        <ArrowDownToLine size={16} />
        {t('Deposit')}
      </button>

      <button
        type="button"
        onClick={() => {
          setActiveModal('withdraw');
          onWithdraw?.();
        }}
        className="flex h-11 px-5 items-center justify-center gap-1 rounded-lg bg-[#171717] text-sm font-normal leading-4.5 text-white transition-colors hover:bg-[#232323]"
      >
        <ArrowUpToLine size={16} />
        {t('Withdraw')}
      </button>

      {showHistoryButton && (
        <button
          type="button"
          aria-label={t('Transaction History')}
          onClick={() => {
            setHistoryOpen(true);
            onHistory?.();
          }}
          className="flex size-11 items-center justify-center rounded-lg bg-[#171717] text-white transition-colors hover:bg-[#232323]"
        >
          <Clock size={20} strokeWidth={1.5} />
        </button>
      )}

      <DepositModal
        open={activeModal === 'deposit'}
        onClose={() => setActiveModal(null)}
        {...depositProps}
      />
      <WithdrawModal
        open={activeModal === 'withdraw'}
        onClose={() => setActiveModal(null)}
        {...withdrawProps}
      />

      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        historyProps={historyProps}
      />
    </div>
  );
}
